// src/lib/chainModel.js — the card's chain model, PURE (no React, no DOM) so it can be
// tested under `node --test` like deskFeed/propertyEpisodes. Presentation lives in
// ChainPanel (components/desk/HistoryCards.jsx); this file decides only WHAT IS TRUE.
// ── The chain (design 2b) and the duplicates band (6a) ───────────────────────
// Design handoff 2026-08-04 (`design/Card design refinement options.zip`), answered against
// the live data in `design/CARD_FACE_ANSWERS_AND_QUESTIONS_2026-08-04.md`. The one fact that
// shapes all of this:
//
//   ⚠️ WE KNOW WHO COPIED WHOM ONLY WHEN THE COPY IS OURS. `derived_from` is declared by the
//   extension at capture time (eyewitness, confidence 1.0) — measured 2026-08-04: 13,534 edges,
//   ~100% of children authored by our own agents. Another agency's repost, or Korter copying
//   SS.ge, arrives as a `same_as` match with NO direction: it says "one flat", never "this one
//   first". 3,221 of 32,344 properties carry more than one active listing.
//
// So there are TWO STATES AND THEY MUST LOOK DIFFERENT — never one component that degrades
// (designer's ruling, turn 6). A tree with connectors means "we know the order"; a flat band
// with no connectors means "these match, nobody claims to be first". The absence of the lines
// IS the statement, which is why there is no per-row "source unknown" label.
//
// ⚠️ AND NEVER INFER DIRECTION FROM A DATE. First-seen here is OUR CRAWL ORDER: whichever ad
// we happened to scrape first would masquerade as the original, and the card would confidently
// name the wrong person to call. Order is used for HANDLES and for nothing else.
export function buildChain(items) {
  const rows = []
  for (const i of items || []) {
    const l = i.listing
    if (!l?.listing_id) continue
    rows.push({
      id: l.listing_id,
      ours: !!l.is_reshare,
      parent: l.origin_listing_id || null,
      source: l.source || null,
      url: l.source_url || null,
      price: l.price != null ? Number(l.price) : null,
      currency: l.currency || l.price_unit || null,
      // Who is behind this posting. A reshare's author IS the reposting agent; a plain scrape's
      // author is the other party, so the capturer is the one who identifies ours.
      who: (l.is_reshare ? l.author_name : (l.author_name || l.scraped_by_name)) || null,
      at: l.posted_at || l.listed_at || i.created_at || null,
      closed: l.status && l.status !== 'active',
    })
  }
  if (rows.length < 2) return null
  // HANDLES: first-seen order, never reused, never renumbered (designer rule 3) — a closed O2
  // leaves a visible gap and the gap is information. `id` breaks ties so the order is stable
  // across renders rather than accidentally stable.
  // ⚠️ This is DERIVED per render from the rows the card holds, so a listing that drops out of
  // the cascade entirely would renumber the rest. Durable handles need to be assigned once and
  // stored; see B29x in core/BACKLOG.md.
  const ordered = [...rows].sort((a, b) =>
    String(a.at || '').localeCompare(String(b.at || '')) || a.id.localeCompare(b.id))
  let o = 0, r = 0
  for (const row of ordered) row.handle = row.ours ? `R${++r}` : `O${++o}`
  const byId = new Map(ordered.map(x => [x.id, x]))
  // An edge only counts when BOTH ends are on this card. A parent that is off-cascade is a
  // pointer we cannot draw, and half a tree reads as a complete one.
  for (const row of ordered) if (!byId.has(row.parent)) row.parent = null
  const known = ordered.some(x => x.parent)
  // Depth-first, roots first, so a child always renders under the row it came from.
  const kids = new Map()
  for (const row of ordered) if (row.parent) (kids.get(row.parent) ?? kids.set(row.parent, []).get(row.parent)).push(row)
  const out = []
  const walk = (row, depth) => {
    out.push({ ...row, depth })
    for (const k of kids.get(row.id) || []) walk(k, depth + 1)
  }
  for (const row of ordered) if (!row.parent) walk(row, 0)
  // The root's price is the reference for every delta — "what has it moved to since the
  // original ad". Only within one currency: a USD/GEL delta is a made-up number.
  const root = out[0]
  for (const row of out) {
    row.delta = (known && root && row !== root && row.price != null && root.price != null
      && row.currency === root.currency && root.price > 0)
      ? Math.round(((row.price - root.price) / root.price) * 100)
      : null
  }
  return {
    known,
    rows: out,
    sources: out.filter(x => !x.ours).length,
    reposts: out.filter(x => x.ours).length,
    platforms: new Set(out.map(x => x.source).filter(Boolean)).size,
  }
}
